"use client";

import React, { useMemo } from "react";
import { ArrowRight, ShieldCheck, SlidersHorizontal, CircleCheck, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function ApplicationsHero() {
  const {
    lang
  } = useLanguage();
  const copy = useMemo(() => ({
    badgeCategory: pick(lang, {
      en: "Applications & Processes",
      zh: "工业应用与工艺流程",
      es: "Aplicaciones y procesos",
      de: "Anwendungen & Verfahren",
      fr: "Applications et procédés",
      pt: "Aplicações e processos"
    }),
    h1: pick(lang, {
      en: "Precision mesh solutions for demanding industrial separation",
      zh: "严苛工业分离工况的精密丝网方案",
      es: "Soluciones de malla de precisión para separación industrial exigente",
      de: "Präzisions-Drahtgewebelösungen für anspruchsvolle industrielle Trennprozesse",
      fr: "Solutions de toile métallique de précision pour la séparation industrielle exigeante",
      pt: "Soluções em malha de precisão para separação industrial exigente"
    }),
    lead: pick(lang, {
      en: "MutualFilter provides high-performance stainless steel woven wire mesh, custom sintered filter media, and wedge wire screens engineered for demanding industrial separation conditions. Designed for polymer extrusion, chemical processing, liquid straining, and powder screening, our components maintain rigorous aperture tolerance and mechanical integrity. We deliver technical support and custom fabrication aligned with established standards.",
      zh: "MutualFilter 提供高性能不锈钢编织丝网、定制多层烧结滤材以及楔形丝滤网，专为严苛工业流体与分离工况设计制造。产品覆盖聚合物挤出、化工工艺、液体澄清与粉体筛分等关键流程，具备严格的孔径公差与结构强度。我们依据行业公认标准提供工程支持与定制加工服务。",
      es: "MutualFilter suministra malla de alambre tejida de acero inoxidable de alto rendimiento, medios filtrantes sinterizados a medida y tamices de alambre en cuña para condiciones exigentes de separación industrial. Diseñados para extrusión de polímeros, procesamiento químico, filtración de líquidos y tamizado de polvos, nuestros componentes mantienen tolerancias dimensionales rigurosas e integridad mecánica.",
      de: "MutualFilter liefert Hochleistungs-Drahtgewebe aus Edelstahl, anwendungsspezifische Sinterfiltermedien und Spaltsiebe für anspruchsvolle industrielle Trennbedingungen. Ausgelegt für Polymer-Extrusion, chemische Verfahrenstechnik, Flüssigkeitsfiltration und Pulversiebung gewährleisten unsere Komponenten enge Maschentoleranzen und hohe mechanische Stabilität.",
      fr: "MutualFilter fournit des toiles métalliques tissées en acier inoxydable haute performance, des médias filtrants frittés sur mesure et des crépines à fil profilé pour les procédés de séparation industrielle exigeants. Conçus pour l'extrusion de polymères, les procédés chimiques, la filtration des liquides et le tamisage de poudres, nos composants garantissent une tolérance d'ouverture rigoureuse.",
      pt: "A MutualFilter fornece malha de arame tecida em aço inoxidável de alto desempenho, meios filtrantes sinterizados sob medida e telas de arame em cunha para condições severas de separação industrial. Projetados para extrusão de polímeros, processamento químico, clarificação de líquidos e peneiramento de pós, nossos componentes mantêm tolerâncias rigorosas."
    }),
    technicalResources: pick(lang, {
      en: "Technical Resources",
      zh: "技术资料库",
      es: "Recursos técnicos",
      de: "Technische Ressourcen",
      fr: "Ressources techniques",
      pt: "Recursos técnicos"
    }),
    badgeOverlay: pick(lang, {
      en: "Industrial Separation Media",
      zh: "工业分离介质标准",
      es: "Medios de separación industrial",
      de: "Industrielle Trennmedien",
      fr: "Médias de séparation industrielle",
      pt: "Meios de separação industrial"
    }),
    imageAlt: pick(lang, {
      en: "Stainless steel woven wire mesh for industrial filtration",
      zh: "工业过滤不锈钢编织丝网与分离介质",
      es: "Malla de alambre tejida de acero inoxidable para filtración industrial",
      de: "Edelstahl-Drahtgewebe für die industrielle Filtration",
      fr: "Toile métallique tissée en acier inoxydable pour filtration industrielle",
      pt: "Malha de arame tecida em aço inoxidável para filtração industrial"
    }),
    bottomNote: pick(lang, {
      en: "Rolls, cylinders & custom assemblies",
      zh: "支持原卷、滤筒与定制深加工组件",
      es: "Rollos, cilindros y ensamblajes a medida",
      de: "Rollen, Zylinder und maßgeschneiderte Baugruppen",
      fr: "Rouleaux, cylindres et assemblages sur mesure",
      pt: "Rolos, cilindros e montagens sob medida"
    }),
    processSupport: pick(lang, {
      en: "Process Support",
      zh: "工艺咨询",
      es: "Soporte de proceso",
      de: "Prozessberatung",
      fr: "Support procédé",
      pt: "Suporte de processo"
    })
  }), [lang]);
  const technicalParameters = useMemo(() => [{
    label: pick(lang, {
      en: "Woven Mesh Range",
      zh: "编织网规格范围",
      es: "Rango tejido",
      de: "Gewebebereich",
      fr: "Gamme tissée",
      pt: "Faixa tecida"
    }),
    val: "1–635 mesh"
  }, {
    label: pick(lang, {
      en: "Filtration Rating",
      zh: "综合截留精度",
      es: "Rango de filtración",
      de: "Filterfeinheit",
      fr: "Seuil de filtration",
      pt: "Faixa de filtração"
    }),
    val: "1 µm – 25 mm"
  }, {
    label: pick(lang, {
      en: "Sintered Mesh",
      zh: "多层烧结精度",
      es: "Malla sinterizada",
      de: "Sintergewebe",
      fr: "Toile frittée",
      pt: "Malha sinterizada"
    }),
    val: "1–200 µm"
  }, {
    label: pick(lang, {
      en: "Wedge Wire Slots",
      zh: "楔形丝缝隙范围",
      es: "Ranuras de alambre en cuña",
      de: "Spaltsieb-Spaltweiten",
      fr: "Fentes de fil profilé",
      pt: "Fendas de arame em cunha"
    }),
    val: "0.02–5.0 mm"
  }], [lang]);
  return <section id="applicationshero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges row: exactly two outline badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.badgeCategory}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.h1}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors bg-card">
                <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.technicalResources}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Spec Card (5 Columns on lg) */}
          <Reveal delay={120} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-industrial-photograph-of-st-8f0eae.png" alt={copy.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                {/* Overlay Chip */}
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.badgeOverlay}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.bottomNote}</span>
                  </div>
                  <Link to="/ContactPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.processSupport}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip Below Grid */}
        <div className="mf-spec-strip">
          {technicalParameters.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value font-mono">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}